// EAIRI — internationalisation.
//
// French is the language of record: `fr` is the source, `en` is a mirror, and
// the fallback runs FR-first deliberately. A missing English string should
// surface as French rather than as a raw key — the audience reads both, and a
// key on screen in a board-facing product is worse than the other language.
//
// A test asserts both bundles carry every frozen error code and identical
// interpolation placeholders, so the two cannot drift.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import fr from "../locales/fr.json";
import en from "../locales/en.json";

export const SUPPORTED = ["fr", "en"] as const;
export type Lang = (typeof SUPPORTED)[number];

export const isLang = (v: unknown): v is Lang =>
  typeof v === "string" && (SUPPORTED as readonly string[]).includes(v);

/** URL first, then a stored choice, then the browser, then French. */
export function detectLang(
  search: string | undefined = typeof location === "undefined" ? "" : location.search,
  stored: string | null = null,
  navLang: string | undefined = typeof navigator === "undefined" ? undefined : navigator.language,
): Lang {
  const fromUrl = new URLSearchParams(search ?? "").get("lang");
  if (isLang(fromUrl)) return fromUrl;
  if (isLang(stored)) return stored;
  if (navLang && navLang.toLowerCase().startsWith("en")) return "en";
  return "fr";
}

export const LANG_KEY = 'eairi.lang';

export function storedLang(): string | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage.getItem(LANG_KEY); }
  catch { return null; }
}

export function rememberLang(lang: Lang): void {
  try { localStorage.setItem(LANG_KEY, lang); } catch { /* storage unavailable */ }
}

// The default reads the stored choice back. Without it the switcher writes a
// preference that nothing ever consults, and a page navigation silently
// reverts to French.
export function initI18n(lang: Lang = detectLang(undefined, storedLang())) {
  void i18n.use(initReactI18next).init({
    resources: { fr: { translation: fr }, en: { translation: en } },
    lng: lang,
    fallbackLng: "fr",
    interpolation: {
      // React escapes for us; double-escaping mangles the apostrophes and
      // guillemets that French copy is full of.
      escapeValue: false,
    },
    returnNull: false,
  });
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  return i18n;
}

/** Name the copy that explains a refusal.
 *
 *  Returns a key and its parameters rather than taking a translator: the API
 *  error already carries the key, so this needs no coupling to i18next's
 *  types, and the caller does the one thing it is already set up to do.
 */
export function errorMessage(
  err: { i18nKey?: string; details?: Record<string, unknown> } | null | undefined,
): { key: string; params: Record<string, unknown> } {
  return {
    key: err?.i18nKey ?? 'eairi.errors.invariant_violation',
    params: err?.details ?? {},
  };
}

export default i18n;
